'use client';
import React, { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  name: string;
  solution: string;
  phone: string;
  email: string;
}

const solutionOptions = [
  'Choose a solution...',
  'Budgeting & Forecasting',
  'Management Accounts',
  'Performance Appraisal',
  'Project Appraisal',
  'Data Analytics',
  'Advanced Excel Training',
  'Uniform Fuel Pricing',
  'Why Quantrix',
  'General Enquiry',
];

const guarantees = [
  { icon: '🛡️', text: 'Completely free — no credit card required' },
  { icon: '↩️', text: '45-Day Money-Back Guarantee on all solutions' },
  { icon: '⚡', text: 'Response within 24 hours, no spam' },
];

export default function ContactFormSection() {
  const [form, setForm] = useState<FormData>({ name: '', solution: '', phone: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isValid =
    form.name.trim() &&
    form.solution &&
    form.solution !== 'Choose a solution...' &&
    (form.phone.trim() || form.email.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    setSubmitError(null);

    try {
      const supabase = createClient();
      const { error } = await supabase.from('demo_bookings').insert({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        solution: form.solution,
        status: 'new',
        priority: 'medium',
        source: 'website',
        assignee: '',
        notes: '',
      });

      if (error) {
        console.error('Booking submission error:', error.message);
        setSubmitError('Something went wrong. Please try again or contact us directly.');
        return;
      }

      setSubmitted(true);
    } catch (err: unknown) {
      console.error('Unexpected error:', err);
      setSubmitError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden py-24"
      style={{ background: 'linear-gradient(160deg, #071318 0%, #0d2229 50%, #1a3a44 100%)' }}
    >
      {/* Background orbs */}
      <div
        className="absolute top-0 right-0 w-[500px] h-[500px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(141,197,62,0.1) 0%, transparent 70%)', filter: 'blur(80px)' }}
      />
      <div
        className="absolute bottom-0 left-0 w-[400px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(108,45,48,0.12) 0%, transparent 70%)', filter: 'blur(80px)' }}
      />
      <div className="absolute inset-0 pattern-dots opacity-20 pointer-events-none" />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Info */}
          <div>
            <div className="tag-pill bg-[#8dc53e]/15 text-[#8dc53e] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8dc53e]" />
              Free Discovery Call
            </div>
            <h2
              className="font-bold text-white mb-5"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              Book your spot.<br />
              <span className="text-[#8dc53e]">No pressure, just solutions.</span>
            </h2>
            <p className="text-white/60 leading-relaxed mb-10 max-w-[440px]">
              We&apos;ll send a single follow-up within 24 hours of your enquiry. No spam, no sales calls. Cancel any time.
            </p>

            {/* Guarantees */}
            <div className="flex flex-col gap-4 mb-10">
              {guarantees.map((item) => (
                <div key={item.text} className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
                    style={{ background: 'rgba(141,197,62,0.12)', border: '1px solid rgba(141,197,62,0.2)' }}
                  >
                    {item.icon}
                  </div>
                  <span className="text-sm text-white/70">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Testimonial quote */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <div className="text-2xl text-[#8dc53e] font-serif mb-3">&ldquo;</div>
              <p className="text-white/70 text-sm leading-relaxed italic mb-4">
                Celsoft experience has been an eye-opener. I recommend it to anyone who is in finance.
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#1f525d] flex items-center justify-center text-white font-bold text-xs">AM</div>
                <div>
                  <p className="text-white text-xs font-semibold">Alfred Mandara</p>
                  <p className="text-white/40 text-xs">Reserve Bank of Zimbabwe</p>
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="mt-8 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1f525d]/60 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <a href="mailto:celsoft@celsoftcorporation.com" className="text-sm text-white/60 hover:text-[#8dc53e] transition-colors">
                  celsoft@celsoftcorporation.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1f525d]/60 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <a href="tel:+2637727463291" className="text-sm text-white/60 hover:text-[#8dc53e] transition-colors">
                  (+263) 772 746 329
                </a>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className="rounded-2xl p-8"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(20px)' }}
          >
            {submitted ? (
              <div className="text-center py-12">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                  style={{ background: 'rgba(141,197,62,0.2)', border: '1px solid rgba(141,197,62,0.3)' }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8dc53e" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Enquiry Received!</h3>
                <p className="text-sm text-white/60">
                  Thank you, {form.name}. We&apos;ll be in touch within 24 hours.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-white mb-1">Book a Discovery Call</h3>
                <p className="text-sm text-white/50 mb-7">Three fields. Thirty seconds. Done.</p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-semibold text-white/60 mb-2 uppercase tracking-widest">
                      Your Name
                    </label>
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(141,197,62,0.5)'; e.target.style.background = 'rgba(255,255,255,0.1)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; e.target.style.background = 'rgba(255,255,255,0.08)'; }}
                    />
                  </div>

                  {/* Solution */}
                  <div>
                    <label className="block text-xs font-semibold text-white/60 mb-2 uppercase tracking-widest">
                      Solution of Interest
                    </label>
                    <select
                      value={form.solution}
                      onChange={(e) => setForm({ ...form, solution: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl text-sm text-white focus:outline-none transition-all duration-200 appearance-none"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                    >
                      {solutionOptions.map((opt) => (
                        <option key={opt} value={opt === 'Choose a solution...' ? '' : opt} style={{ background: '#0d2229', color: 'white' }}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-white/60 mb-2 uppercase tracking-widest">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      placeholder="+263 7XX XXX XXX"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(141,197,62,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold text-white/60 mb-2 uppercase tracking-widest">
                      Email Address
                    </label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3.5 rounded-xl text-sm text-white placeholder:text-white/30 focus:outline-none transition-all duration-200"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(141,197,62,0.5)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                    />
                  </div>

                  {submitError && (
                    <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-3">
                      {submitError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!isValid || loading}
                    className="flex items-center justify-center gap-2 w-full px-6 py-4 font-bold rounded-xl text-[#0d2229] text-sm uppercase tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #8dc53e, #a8d85a)', boxShadow: isValid && !loading ? '0 8px 25px rgba(141,197,62,0.3)' : 'none' }}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Book My Discovery Call
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
